import { useState, useEffect, useCallback } from 'react';
import { useParams, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Sidebar from '../../components/dashboard/Sidebar';
import OverviewPanel from './OverviewPanel';
import WelcomeConfig from './WelcomeConfig';
import PokemonConfig from './PokemonConfig';
import ModerationConfig from './ModerationConfig';
import RolesPanel from './RolesPanel';
import WebhookConfig from './WebhookConfig';
import LevelingConfig from './LevelingConfig';
import { Menu, Loader2, ShieldAlert, RefreshCw, ArrowLeft } from 'lucide-react';

interface ErrorDetails {
  status: number;
  message: string;
}

export default function ServerDashboard() {
  const { guildId } = useParams<{ guildId: string }>();
  const { guilds, isAuthenticated, token, refreshGuilds, loading: authLoading } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [guildData, setGuildData] = useState<any>(null);
  const [error, setError] = useState<ErrorDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const guild = guilds.find((g) => g.id === guildId);

  const fetchGuild = useCallback(async (isManualRefresh = false) => {
    if (!guildId || !token) return;
    
    if (isManualRefresh) setIsRefreshing(true);
    else setLoading(true);
    setError(null);

    try {
      const API_BASE = import.meta.env.VITE_API_URL || '';
      const res = await fetch(`${API_BASE}/api/dashboard/${guildId}/overview${isManualRefresh ? '?refresh=true' : ''}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.ok) {
        setGuildData(await res.json());
        setError(null);
      } else {
        const data = await res.json().catch(() => ({}));
        setError({
          status: res.status,
          message: data.error || `Failed to load server data (HTTP ${res.status})`
        });
      }
    } catch (err: any) {
      console.error('Failed to fetch guild data:', err);
      setError({
        status: 0,
        message: err?.message || 'Network error: Failed to connect to server.'
      });
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, [guildId, token]);

  // Initial fetch or dev mock bypass
  useEffect(() => {
    if (!guildId || !token) return;

    if (token === 'dev-token' && import.meta.env.DEV) {
      setGuildData({
        id: guildId,
        name: 'Dev Server',
        icon: null,
        memberCount: 420,
        presenceCount: 87,
        channels: [
          { id: '111', name: 'general', position: 0 },
          { id: '222', name: 'mod-log', position: 1 },
          { id: '333', name: 'welcome', position: 2 },
          { id: '444', name: 'bot-commands', position: 3 },
          { id: '555', name: 'pokemon', position: 4 },
        ],
        roles: [
          { id: '100', name: 'Admin', color: '#e74c3c', position: 4 },
          { id: '200', name: 'Moderator', color: '#3498db', position: 3 },
          { id: '300', name: 'Member', color: '#2ecc71', position: 2 },
          { id: '400', name: 'Muted', color: '#95a5a6', position: 1 },
        ],
      });
      setLoading(false);
      setError(null);
      return;
    }

    fetchGuild();
  }, [guildId, token, fetchGuild]);

  // Re-fetch overview once auth loading finishes after a manual refresh/sync
  useEffect(() => {
    if (error && !authLoading && isRefreshing) {
      fetchGuild(true).finally(() => setIsRefreshing(false));
    }
  }, [authLoading, error, isRefreshing, fetchGuild]);

  // ── Scroll position persistence ─────────────────────────────
  // Restore scroll when navigating to a page we've visited before in this session
  useEffect(() => {
    const key = `scroll:${location.pathname}`;
    const saved = sessionStorage.getItem(key);
    if (saved !== null) {
      requestAnimationFrame(() => window.scrollTo(0, Number(saved)));
    } else {
      window.scrollTo(0, 0);
    }

    // Save scroll position when leaving this page
    const onScroll = () => {
      sessionStorage.setItem(key, String(window.scrollY));
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [location.pathname]);

  if (!isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  if (loading) {
    return (
      <div className="pt-24 pb-20 min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!guild) {
    return (
      <div className="pt-24 pb-20 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-400 text-lg mb-2">Server not found</p>
          <p className="text-gray-600 text-sm">You may not have access to this server.</p>
        </div>
      </div>
    );
  }

  const handleSync = () => {
    setIsRefreshing(true);
    refreshGuilds();
  };

  return (
    <div className="flex min-h-screen pt-[72px]">
      <Sidebar
        guildId={guildId!}
        guildName={guild.name}
        guildIcon={guild.icon}
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      <main className="flex-1 min-w-0">
        {/* Mobile header */}
        <div className="lg:hidden sticky top-[72px] z-30 bg-dark-900/95 backdrop-blur-xl border-b border-white/[0.05] px-4 py-3 flex items-center gap-3">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 rounded-xl text-gray-400 hover:text-white hover:bg-white/[0.06] transition-colors"
          >
            <Menu className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-2.5 min-w-0 flex-1">
            {guild.icon ? (
              <img src={guild.icon} alt="" className="w-7 h-7 rounded-lg shrink-0" />
            ) : (
              <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-primary/30 to-secondary/30 flex items-center justify-center text-white text-xs font-bold shrink-0">
                {guild.name.charAt(0)}
              </div>
            )}
            <span className="text-white font-semibold text-sm truncate">{guild.name}</span>
          </div>
        </div>

        <div className="p-4 sm:p-6 lg:p-8">
          {error ? (
            <div className="max-w-md mx-auto my-12 bg-dark-800/60 border border-white/[0.05] rounded-3xl p-8 text-center backdrop-blur-xl shadow-2xl relative overflow-hidden">
              <div className="absolute -top-12 -left-12 w-24 h-24 bg-red-500/10 rounded-full blur-2xl pointer-events-none" />
              <div className="absolute -bottom-12 -right-12 w-24 h-24 bg-primary/10 rounded-full blur-2xl pointer-events-none" />

              <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-red-500/10 border border-red-500/20 flex items-center justify-center text-red-400">
                <ShieldAlert className="w-8 h-8" />
              </div>

              <h3 className="text-xl font-display font-bold text-white mb-2">Access Denied</h3>
              <p className="text-gray-400 text-sm mb-6 leading-relaxed">
                {error.status === 403 
                  ? "You need Manage Server or Administrator permissions to configure this server." 
                  : error.message}
              </p>

              <div className="bg-white/[0.02] border border-white/[0.04] rounded-2xl p-4 mb-6 text-left text-xs text-gray-400 space-y-2.5">
                <div className="flex items-start gap-2.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0 mt-1.5" />
                  <p>Make sure you have <strong>Manage Server</strong> or <strong>Administrator</strong> permission in Discord.</p>
                </div>
                <div className="flex items-start gap-2.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0 mt-1.5" />
                  <p>If you were recently granted permission, click the sync button below to update your session permissions.</p>
                </div>
                <div className="flex items-start gap-2.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0 mt-1.5" />
                  <p>Verify that the bot has been invited and has sufficient permissions in the server.</p>
                </div>
              </div>

              <div className="flex flex-col gap-2.5">
                <button
                  onClick={handleSync}
                  disabled={isRefreshing || authLoading}
                  className="w-full flex items-center justify-center gap-2 px-5 py-3 rounded-2xl text-sm font-semibold text-white bg-primary/20 border border-primary/30 hover:bg-primary/35 transition-all disabled:opacity-40"
                >
                  <RefreshCw className={`w-4 h-4 ${isRefreshing || authLoading ? 'animate-spin' : ''}`} />
                  {isRefreshing || authLoading ? 'Syncing permissions…' : 'Sync Discord Permissions'}
                </button>
                
                <button
                  onClick={() => window.location.href = '/dashboard'}
                  className="w-full flex items-center justify-center gap-2 px-5 py-3 rounded-2xl text-sm font-semibold text-gray-400 hover:text-white bg-white/[0.02] border border-white/[0.04] hover:bg-white/[0.05] transition-all"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to Servers
                </button>
              </div>
            </div>
          ) : (
            <Routes>
              <Route index element={<OverviewPanel guildData={guildData} guild={guild} />} />
              <Route path="welcome" element={<WelcomeConfig guildId={guildId!} guildData={guildData} />} />
              <Route path="moderation" element={<ModerationConfig guildId={guildId!} guildData={guildData} />} />
              <Route path="pokemon" element={<PokemonConfig guildId={guildId!} guildData={guildData} />} />
              <Route path="roles" element={<RolesPanel guildData={guildData} guild={guild} />} />
              <Route path="webhooks" element={<WebhookConfig guildId={guildId!} guildData={guildData} />} />
              <Route path="leveling" element={<LevelingConfig guildId={guildId!} guildData={guildData} />} />
            </Routes>
          )}
        </div>
      </main>
    </div>
  );
}

